import React from 'react'
import { withStyles, createStyles, useIsMobile } from '@paintscout/ui'
import type { MuiTheme as Theme, WithStyles, Action, TileProps } from '@paintscout/ui'
import { TileList } from '@paintscout/ui-business'
import type { ValueType } from '@paintscout/ui-business'
import EditIcon from '@material-ui/icons/Edit'
import DeleteIcon from '@material-ui/icons/Delete'
import CopyIcon from '@material-ui/icons/FileCopy'

import type { Axis } from 'react-sortable-hoc'

const styles = (_theme: Theme) => {
  return createStyles({
    root: {},
    tiles: {},
    button: {
      display: 'flex',
      justifyContent: 'flex-end',
      paddingRight: '1.5rem',
      paddingBottom: '0.5rem'
    }
  })
}

export interface OptionsTileListProps extends WithStyles<typeof styles> {
  title: string
  subtitle?: string
  items: any[]
  createTitle?: string
  isMobile?: boolean
  showCreate?: boolean
  disabled?: boolean
  actions?: Action[]
  selected?: string[]
  setSelected?: (selected: string[]) => void

  onCreateClick?: (event: React.MouseEvent<HTMLElement>) => void
  onReorder?: (items: any) => void
  onEditBulkItems?: (event: any, keys: ValueType[]) => void
  onAssignBulkItems?: (event: any, keys: ValueType[]) => void
  onEditItem?: (event: any, key: string | number) => void
  onCopyItem?: (event: any, key: string | number) => void
  onDeleteBulkItems?: (event: any, keys: ValueType[]) => void
  onDeleteItem?: (event: any, key: string | number) => void
  onItemAction?: (event: any, key: string | number, action: string) => void

  permanent?: string[]

  noItemTitle?: string
  noItemHelpText?: string
  addButtonVariant?: 'outlined' | 'text'
  fullAddButton?: boolean

  axis?: Axis

  hideReorderSwitch?: boolean

  allowEmpty?: boolean
  tileClasses?: TileProps['tileClasses']
  showActive?: boolean
}

function withMobileCheck(Component: React.ComponentType<any>) {
  return function WithMobileCheck(props: any) {
    const isMobile = useIsMobile()
    return <Component {...props} isMobile={isMobile} />
  }
}

export interface OptionsTileListState {
  reorderMode: boolean
  bulkEditMode: boolean
}

class OptionsTileList extends React.Component<OptionsTileListProps, OptionsTileListState> {
  constructor(props: OptionsTileListProps) {
    super(props)
    this.state = {
      bulkEditMode: false,
      reorderMode: props.hideReorderSwitch
    }
  }

  public render() {
    const {
      items,
      title,
      subtitle,
      classes,
      showCreate,
      permanent,
      axis,
      disabled,
      setSelected,
      allowEmpty = true,
      tileClasses,
      fullAddButton = true,
      showActive = true
    } = this.props
    const { reorderMode, bulkEditMode } = this.state
    const editAction = {
      key: 'edit',
      label: 'Edit',
      icon: EditIcon
    }

    const copyAction = {
      key: 'copy',
      label: 'Copy',
      icon: CopyIcon
    }

    const deleteAction =
      items.length === 1 && !allowEmpty
        ? null
        : {
            key: 'delete',
            label: 'Delete',
            icon: DeleteIcon
          }

    return (
      <TileList
        addButtonVariant={this.props.addButtonVariant}
        fullAddButton={fullAddButton}
        classes={{
          root: classes.root,
          tiles: classes.tiles
        }}
        tileClasses={tileClasses}
        items={items}
        disabled={disabled}
        title={title}
        subtitle={subtitle}
        draggable={!!this.props.onReorder}
        showActive={showActive}
        dragAxis={axis}
        actions={(item) => {
          if (
            item.name === 'all/default' ||
            item.label === 'Default' ||
            (permanent && permanent.includes(item.key as string))
          ) {
            return [editAction]
          } else {
            return [
              this.props.onEditItem ? editAction : null,
              this.props.onCopyItem ? copyAction : null,
              this.props.onDeleteItem ? deleteAction : null,
              ...(this.props.actions ? this.props.actions : [])
            ].filter((item) => item)
          }
        }}
        onSelect={(event: any, selected: ValueType[] | ValueType) => {
          if (!bulkEditMode) {
            this.props.onEditItem(event, selected as ValueType)
          }
        }}
        variant={bulkEditMode ? 'multiselect' : 'standard'}
        onToggleBulkEditMode={() => {
          this.setState({ bulkEditMode: !bulkEditMode, reorderMode: false })
          if (setSelected) {
            setSelected([])
          }
        }}
        onAssignBulkItems={this.props.onAssignBulkItems}
        onEditBulkItems={this.props.onEditBulkItems}
        bulkEditMode={this.state.bulkEditMode}
        selected={this.props.selected}
        onDeleteBulkItems={this.props.onDeleteBulkItems}
        createButton={showCreate && this.props.createTitle}
        onActionClick={this.handleActionClick}
        onReorder={this.props.onReorder}
        onCreate={this.props.onCreateClick}
        noItemTitle={this.props.noItemTitle}
        noItemHelpText={this.props.noItemHelpText}
        subLabelField={'sublabel'}
        reorderMode={this.state.reorderMode}
        onToggleReorderMode={() => {
          this.setState({ reorderMode: !reorderMode, bulkEditMode: false })
          if (setSelected) {
            setSelected([])
          }
        }}
        hideReorderSwitch={this.props.hideReorderSwitch}
      />
    )
  }

  public handleActionClick = (event: React.MouseEvent<HTMLElement>, actionName: string, key: string | number) => {
    if (actionName === 'edit') {
      this.props.onEditItem(event, key)
    } else if (actionName === 'copy') {
      this.props.onCopyItem(event, key)
    } else if (actionName === 'delete') {
      this.props.onDeleteItem(event, key)
    } else if (this.props.onItemAction) {
      this.props.onItemAction(event, key, actionName)
    }
  }
}

export default withMobileCheck(withStyles(styles)(OptionsTileList))
